import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Alert,
  Link,
  Grid,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';

const Register: React.FC = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [formData, setFormData] = useState({
    benutzername: '',
    email: '',
    passwort: '',
    passwortWiederholen: '',
    vorname: '',
    nachname: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validierung
    if (formData.passwort !== formData.passwortWiederholen) {
      setError('Die Passwörter stimmen nicht überein.');
      return;
    }

    if (formData.passwort.length < 8) {
      setError('Das Passwort muss mindestens 8 Zeichen lang sein.');
      return;
    }

    setLoading(true);

    try {
      await register({
        benutzername: formData.benutzername,
        email: formData.email,
        passwort: formData.passwort,
        passwortBestaetigung: formData.passwortWiederholen,
        vorname: formData.vorname || undefined,
        nachname: formData.nachname || undefined,
      });

      // Backend gibt bei Erfolg das BenutzerDto zurück (kein success-Feld)
      // Wenn wir hier ankommen, war die Registrierung erfolgreich
      setSuccess(
        'Registrierung erfolgreich! Sie werden zur Anmeldeseite weitergeleitet...'
      );
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (err: any) {
      console.error('Register error:', err);

      // Fehlerbehandlung für Validierungsfehler vom Backend
      if (err.response?.data?.errors && Array.isArray(err.response.data.errors)) {
        setError(err.response.data.errors.join(', '));
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('Registrierung fehlgeschlagen. Bitte versuchen Sie es erneut.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
          {/* Skeena Logo */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <img
              src="/logo.svg"
              alt="Skeena Logo"
              style={{ height: '60px', maxWidth: '100%' }}
            />
          </Box>

          <Typography component="h1" variant="h5" align="center" gutterBottom>
            Registrieren
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  autoComplete="given-name"
                  name="vorname"
                  fullWidth
                  id="vorname"
                  label="Vorname"
                  value={formData.vorname}
                  onChange={handleChange}
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="nachname"
                  label="Nachname"
                  name="nachname"
                  autoComplete="family-name"
                  value={formData.nachname}
                  onChange={handleChange}
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  id="benutzername"
                  label="Benutzername"
                  name="benutzername"
                  autoComplete="username"
                  value={formData.benutzername}
                  onChange={handleChange}
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  id="email"
                  label="E-Mail-Adresse"
                  name="email"
                  autoComplete="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  name="passwort"
                  label="Passwort"
                  type="password"
                  id="passwort"
                  autoComplete="new-password"
                  value={formData.passwort}
                  onChange={handleChange}
                  disabled={loading}
                  helperText="Mindestens 8 Zeichen"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  name="passwortWiederholen"
                  label="Passwort wiederholen"
                  type="password"
                  id="passwortWiederholen"
                  autoComplete="new-password"
                  value={formData.passwortWiederholen}
                  onChange={handleChange}
                  disabled={loading}
                />
              </Grid>
            </Grid>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={
                loading ||
                !formData.benutzername ||
                !formData.email ||
                !formData.passwort ||
                !formData.passwortWiederholen
              }
            >
              {loading ? 'Registrierung läuft...' : 'Registrieren'}
            </Button>
            <Box sx={{ textAlign: 'center' }}>
              <Link component={RouterLink} to="/login" variant="body2">
                Bereits registriert? Anmelden
              </Link>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Register;
